import { SetStateAction, useState } from "react";
import { Button, ButtonGroup } from "@mui/material";
import { IMeal, WeekDaysEnum } from "../types/types";
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';

export const MealItem = ({
  meal,
  setCart,
  menu,
  addingToMenu,
}: {
  meal: IMeal,
  setCart: (value: SetStateAction<IMeal[]>) => void,
  menu?: boolean,
  addingToMenu?: number, // activeMenuTab
}) => {
  const [expanded, setExpanded] = useState(false);
  const addMeal = (meal: IMeal) => {
    setCart((prev) => {
      if (addingToMenu !== undefined) {
        return [...prev, {
          ...meal, quantity: 1, id: `${meal.id}__clone`, weekday: Object.values(WeekDaysEnum)[addingToMenu]
        }];
      }

      const existing = prev.find((p) => p.name === meal.name);
      if (existing) {
        return prev.map((p) =>
          p.name === meal.name
            ? { ...p, quantity: (p.quantity || 0) + 1 }
            : p
        );
      }

      return [...prev, {
        ...meal, quantity: 1, ingredients: meal.ingredients.map((ing) => ({
          ...ing,
          multiplier: ing.quantity
        }))
      }];
    });
  };

  const increaseQuantity = (meal: IMeal) => {
    setCart((meals) => {
      return meals.length ? meals.map((d) => {
        const existing = d.name === meal.name;
        return {
          ...d,
          quantity: existing && d.quantity ? d.quantity + 1 : d.quantity,
          ingredients: [...d.ingredients].map((ing) => ({ ...ing, quantity: ing.quantity + (ing.multiplier ?? 1) }))
        }
      }) : [{ ...meal, quantity: 1 }]
    })
  }

  const decreaseQuantity = (meal: IMeal) => {
    setCart((meals) => {
      return meals.map((d) => {
        const exist = d.name === meal.name;
        if (exist) {
          return {
            ...d,
            quantity: d.quantity! - 1,
            ingredients: [...d.ingredients].map((ing) => ({ ...ing, quantity: ing.quantity - (ing.multiplier ?? 1) }))
          }
        }
        return d
      })
    })
  }

  const toggleExpanded = () => setExpanded(!expanded);

  const handleDeleteMenuItem = (meal: IMeal) => {
    setCart((prev) => prev.filter((p) => p.id !== meal.id));
  }

  const ingredientsPlural = ["2", "3", "4"]
    .includes(String(meal.ingredients.length).slice(-1))
    ? "składniki" : meal.ingredients.length === 1
      ? "składnik" : "składników";

  return (
    <div
      key={meal.name}
      className="bg-white overflow-hidden"
      style={{ width: "100%" }}
    >
      <div className="flex gap-3 p-3">
        <img
          src={meal.imageUrl}
          alt={meal.name}
          className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
          style={menu ? { width: 30, height: 30 } : undefined}
        />
        <div className={menu ? "flex justify-between w-full" : "flex-1 min-w-0"}>
          <h3
            className={menu ? "text-gray-900 mb-1 no-select" : "text-gray-900 mb-1"}
            style={{
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              overflow: "hidden",
              maxWidth: 260
            }}
          >
            {meal.name}
          </h3>
          {menu ? (
            <CloseIcon color="error" className="w-4 h-4" onClick={() => handleDeleteMenuItem(meal)} />
          ) : (
            <p className="text-sm text-gray-500 mb-2">
              {meal.ingredients.length} {ingredientsPlural}
            </p>
          )}
          {!menu && (<div className={menu ? "flex gap-2 justify-between" : "flex gap-2"}>
            <button
              onClick={toggleExpanded}
              className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
            >
              {expanded ? (
                <>
                  <ExpandLessIcon className="w-4 h-4" />
                  Ukryj
                </>
              ) : (
                <>
                  <ExpandMoreIcon className="w-4 h-4" />
                  Składniki
                </>
              )}
            </button>
            {meal.quantity ? (
              <div className="ml-auto">
                <ButtonGroup variant="outlined">
                  <Button sx={{ padding: 0.5, borderRadius: '0.625rem' }} onClick={() => decreaseQuantity(meal)}><RemoveIcon /></Button>
                  <Button sx={{ padding: 0.5 }}>{meal.quantity}</Button>
                  <Button sx={{ padding: 0.5, borderRadius: '0.625rem' }} onClick={() => increaseQuantity(meal)}><AddIcon /></Button>
                </ButtonGroup>
              </div>
            ) : (
              <button
                onClick={() => addMeal(meal)}
                className="ml-auto flex items-center gap-1 bg-blue-600 text-white px-3 rounded-lg text-sm hover:bg-blue-700 transition-colors"
                style={{ paddingTop: 4, paddingBottom: 4, background: "#1f8038" }}
              >
                <AddIcon className="w-4 h-4" />
                Dodaj
              </button>
            )}
          </div>
          )}
        </div>
      </div>
      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-gray-100">
          <p className="text-sm text-gray-600 mb-2">Składniki:</p>
          <ul className="space-y-1">
            {meal.ingredients.map((ingredient) => (
              <li
                key={ingredient.name}
                className="text-sm text-gray-700 flex justify-between gap-1"
              >
                <div className="flex gap-1">
                  <img src={ingredient.imageUrl} alt={ingredient.name} className="w-6 h-6 object-cover rounded-lg flex-shrink-0" />
                  <span style={{ textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden", maxWidth: 220 }}>{ingredient.name}</span>
                </div>
                <span className="text-gray-500">{Number(ingredient.quantity * ingredient.capacity).toFixed(1)} {ingredient.unit}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
